import {
  ActionIcon,
  Box,
  Group,
  Menu,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconChevronDown, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { formatMoney } from 'src/common/currency';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { useCurrency } from 'src/common/hooks/useCurrency';

export const OWNER = 'Owner';

export type OwnerRowValue = {
  id?: string | null;
  client_id?: string | null;
  external_name?: string | null;
  ownership_percentage?: number | null;
};

const EXTERNAL = 'external';

// Products whose backend persists an owners list. The owner-selection step must
// only run for these; other products (crypto, collectibles, pensions) have no
// owner tables, so collecting owners for them would silently drop them on save.
const OWNER_SUPPORTED_PRODUCTS = new Set([
  'Property',
  'InvestmentAndCash',
  'Mortgage',
  'Loan',
  'SpendingAccount',
  'Protection',
]);

export const productSupportsOwners = (product: string): boolean =>
  OWNER_SUPPORTED_PRODUCTS.has(product);

export function ownersSumTo100(owners: OwnerRowValue[]): boolean {
  const sum = owners.reduce((acc, owner) => acc + (Number(owner.ownership_percentage) || 0), 0);
  return Math.abs(sum - 100) < 0.01;
}

// Each owner row is XOR-clean (client_id OR external_name) so the backend's
// exactly-one validator passes; the counter/UI keys are dropped.
export function normaliseOwnersForPayload(owners: OwnerRowValue[]): OwnerRowValue[] {
  return owners.map(owner => {
    const percentage =
      owner.ownership_percentage == null ? null : Number(owner.ownership_percentage);
    const identity = owner.client_id
      ? { client_id: owner.client_id }
      : { external_name: owner.external_name ?? '' };
    return { ...(owner.id ? { id: owner.id } : {}), ...identity, ownership_percentage: percentage };
  });
}

type OwnersPayloadFragment = { owners?: OwnerRowValue[] };

// Resolves the `owners` fragment to merge into a financial-item save payload, shared
// by every product form. Returns null when the captured shares don't total 100% (a
// notification is shown and the caller must abort the save).
//
// A fragment is only emitted for a joint item that actually carries owners. With the
// flag off, a non-joint item, or a joint item with no owners captured, the `owners`
// key is omitted entirely rather than sent as []: EntityManager only derives/reconciles
// when owners is present, so an empty list would delete the backfilled owner row and
// flip is_joint. Omitting it round-trips the item unchanged.
export function resolveOwnersPayload(
  values: FormValuesType,
  product: string,
  flagEnabled: boolean,
): OwnersPayloadFragment | null {
  const isJoint = (values[product] as Record<string, unknown>)?.is_joint === 'true';
  const owners =
    flagEnabled && isJoint ? ((values[OWNER] as OwnerRowValue[] | undefined) ?? []) : [];

  if (owners.length > 0 && !ownersSumTo100(owners)) {
    notifications.show({
      title: 'Ownership shares must total 100%',
      message: 'Adjust each owner’s share so they add up to 100% before saving.',
      color: 'red',
    });
    return null;
  }

  return owners.length > 0 ? { owners: normaliseOwnersForPayload(owners) } : {};
}

type Props = {
  product: string;
  /** The product field holding the item's total value (e.g. 'current_balance' for spending accounts). */
  valueField?: string;
};

export function OwnerSharesSection({ product, valueField = 'value' }: Props) {
  const currency = useCurrency();
  const { form } = useDynamicUI();
  const [searchParams] = useSearchParams();
  const { members } = useFamilyMembers();

  const productValues = (form.getValues()[product] ?? {}) as Record<string, unknown>;
  // Auto-enable joint when the owner-selection step passed joint=true (choosing 2+
  // family members), since useJointOwnerDefault only sets the form value in a later
  // effect and this local switch state wouldn't otherwise pick it up.
  const [isJoint, setIsJoint] = useState(
    () => productValues.is_joint === 'true' || searchParams.get('joint') === 'true',
  );

  // Existing owners (edit, or already seeded) win; otherwise seed from the create
  // flow's owners param. Edit routes never carry an owners param, so no isCreateMode
  // guard is needed.
  const [owners, setOwners] = useState<OwnerRowValue[]>(() => {
    const existing = (form.getValues()[OWNER] as OwnerRowValue[] | undefined) ?? [];
    if (existing.length > 0) return existing;
    const raw = searchParams.get('owners');
    if (raw) {
      try {
        return (JSON.parse(raw) as OwnerRowValue[]).map(owner => ({
          ...owner,
          ownership_percentage: null,
        }));
      } catch {
        return [];
      }
    }
    return [];
  });

  useEffect(() => {
    form.setFieldValue(OWNER, owners);
  }, [owners, form]);

  useEffect(() => {
    form.setFieldValue(`${product}.is_joint`, isJoint ? 'true' : 'false');
  }, [isJoint, product, form]);

  const total = Number(productValues[valueField]) || 0;
  const usedClientIds = new Set(owners.map(owner => owner.client_id).filter(Boolean));

  const memberName = (clientId: string) => {
    const member = members.find(candidate => candidate.clientId === clientId);
    return member ? `${member.client.firstName} ${member.client.lastName}` : 'Unknown owner';
  };

  const toggleJoint = (checked: boolean) => setIsJoint(checked);

  const addOwner = (value: string) => {
    setOwners(previous =>
      value === EXTERNAL
        ? [...previous, { external_name: '', ownership_percentage: null }]
        : [...previous, { client_id: value, ownership_percentage: null }],
    );
  };

  const removeOwner = (index: number) =>
    setOwners(previous => previous.filter((_, i) => i !== index));

  const updateOwner = (index: number, patch: Partial<OwnerRowValue>) =>
    setOwners(previous =>
      previous.map((owner, i) => (i === index ? { ...owner, ...patch } : owner)),
    );

  const shareSum = owners.reduce(
    (acc, owner) => acc + (Number(owner.ownership_percentage) || 0),
    0,
  );
  const sumMatches = Math.abs(shareSum - 100) < 0.01;

  const availableMembers = members.filter(member => !usedClientIds.has(member.clientId));

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Switch
          checked={isJoint}
          onChange={event => toggleJoint(event.currentTarget.checked)}
          label="Joint asset. Enter each owner's share."
        />
        {isJoint && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button variant="tertiary" size="xs" px={9} aria-label="Add owner">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  Add owner
                  <IconChevronDown size={16} color="var(--mantine-color-brand-7)" />
                </span>
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {availableMembers.length > 0 && (
                <Fragment>
                  <Menu.Label>Family members</Menu.Label>
                  {availableMembers.map(member => (
                    <Menu.Item key={member.clientId} onClick={() => addOwner(member.clientId)}>
                      {`${member.client.firstName} ${member.client.lastName}`}
                      {member.isMain ? ' (Main)' : ''}
                    </Menu.Item>
                  ))}
                </Fragment>
              )}
              <Menu.Item onClick={() => addOwner(EXTERNAL)}>Add external owner</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {isJoint && (
        <Stack gap="xs">
          {owners.map((owner, index) => {
            const percentage = Number(owner.ownership_percentage) || 0;
            return (
              <Group key={owner.id ?? index} align="center" wrap="nowrap" gap="md">
                <Box style={{ width: 200 }}>
                  {owner.client_id ? (
                    <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {memberName(owner.client_id)}
                    </Text>
                  ) : (
                    <TextInput
                      placeholder="Owner name"
                      value={owner.external_name ?? ''}
                      onChange={event =>
                        updateOwner(index, { external_name: event.currentTarget.value })
                      }
                    />
                  )}
                </Box>
                <Box
                  style={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    background: 'var(--mantine-color-gray-1)',
                  }}
                >
                  <Box
                    style={{
                      height: 8,
                      borderRadius: 4,
                      width: `${Math.min(100, percentage)}%`,
                      background: 'var(--mantine-color-brand-3)',
                    }}
                  />
                </Box>
                <NumberInput
                  w={90}
                  min={0}
                  max={100}
                  suffix="%"
                  value={owner.ownership_percentage ?? ''}
                  onChange={value =>
                    updateOwner(index, {
                      ownership_percentage: value === '' ? null : Number(value),
                    })
                  }
                />
                <Text size="sm" w={110} ta="right" c="gray.10">
                  {formatMoney((total * percentage) / 100, currency)}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => removeOwner(index)}
                  aria-label="Remove owner"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            );
          })}

          {owners.length > 0 && !sumMatches && (
            <Text size="xs" c="error.6">
              Shares must add up to 100% (currently {shareSum}%).
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}
